import { create } from 'zustand';
import { api, User } from '../lib/api';
import { getApiErrorMessage } from '../lib/errors';
import i18n, { applyLanguage, preAccountLanguage } from '../i18n';
import { useCatStore } from './catStore';

// ============================================================================
// Types
// ============================================================================

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True once loadStoredAuth() has run and the persisted session (if any) has
   *  been restored. Route guards must wait for this before redirecting, so a
   *  hard refresh / deep link isn't bounced to /login (and onward to /dashboard)
   *  while auth is still hydrating from localStorage. */
  hasHydrated: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    password: string,
    fullName: string,
    dateFormat?: User['date_format'],
    units?: { lab_unit: User['lab_unit']; weight_unit: User['weight_unit'] },
    language?: User['language'],
  ) => Promise<void>;
  logout: () => void;
  loadStoredAuth: () => void;
  /** Patch the in-memory user (and the cached copy in localStorage) — used
   *  when verifying email or completing onboarding so the new state is
   *  visible without a re-login round-trip. */
  updateUser: (patch: Partial<User>) => void;
  /** Persist a profile change (e.g. date_format) to the backend, then reflect
   *  it in the in-memory user + localStorage so every screen picks it up. */
  updateProfile: (patch: {
    date_format?: User['date_format'];
    weight_unit?: User['weight_unit'];
    lab_unit?: User['lab_unit'];
    language?: User['language'];
    full_name?: string;
  }) => Promise<void>;
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Check whether a JWT is expired (or will expire within the next 60 seconds).
 * Returns true if the token is expired or unparseable.
 */
function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    if (typeof payload.exp !== 'number') return true;
    // Treat as expired if less than 60 seconds remain
    return payload.exp * 1000 < Date.now() + 60_000;
  } catch {
    return true;
  }
}

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthStore>((set) => ({
  // ========================================================================
  // State
  // ========================================================================

  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  hasHydrated: false,
  error: null,

  // ========================================================================
  // Actions
  // ========================================================================

  async login(email: string, password: string) {
    set({ isLoading: true, error: null });
    try {
      const response = await api.login(email, password);
      const token = response.access_token || (response as { token?: string }).token || '';
      const user = response.user;

      // Drop any cat-store state from the previous session BEFORE writing
      // the new user to localStorage. Otherwise the user-scoped cat-id key
      // would be read against the wrong user id.
      useCatStore.getState().reset();

      // Store in localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));

      // The account's language wins over whatever the login page was showing. It
      // is loaded before the app opens in it, so English never shows in its place.
      await applyLanguage(user.language);

      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: unknown) {
      set({
        error: getApiErrorMessage(error, i18n.t('errors.loginFailed')),
        isLoading: false,
      });
      throw error;
    }
  },

  async signup(
    email: string,
    password: string,
    fullName: string,
    dateFormat?: User['date_format'],
    units?: { lab_unit: User['lab_unit']; weight_unit: User['weight_unit'] },
    language?: User['language'],
  ) {
    set({ isLoading: true, error: null });
    try {
      const response = await api.signup(email, password, fullName, dateFormat, units, language);
      const token = response.access_token || (response as { token?: string }).token || '';
      const user = response.user;

      // Same rationale as login(): purge any leftover cat data from a
      // previous session in this browser before we install the new user.
      useCatStore.getState().reset();

      // Store in localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));

      await applyLanguage(user.language);

      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: unknown) {
      set({
        error: getApiErrorMessage(error, i18n.t('errors.signupFailed')),
        isLoading: false,
      });
      throw error;
    }
  },

  logout() {
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');

    // Reset any other stores that hold per-user state so the next login
    // (potentially with a different account in the same browser tab) starts
    // from a clean slate. This is what fixes the data-isolation bug where a
    // newly-signed-up user could briefly see the previous account's cat.
    useCatStore.getState().reset();

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });
    // Signed out, the device's own choice applies again.
    void applyLanguage(preAccountLanguage());
  },

  loadStoredAuth() {
    const token = localStorage.getItem('authToken');
    const userJson = localStorage.getItem('user');

    // Validate that the token hasn't expired before trusting it. This prevents
    // a flash of authenticated UI followed by an immediate redirect to login on
    // the first API call.
    if (token && userJson && !isTokenExpired(token)) {
      try {
        const user = JSON.parse(userJson) as User;
        // hasHydrated is set together with the restored session so route guards
        // evaluate against the correct auth state in a single commit.
        set({ user, token, isAuthenticated: true, hasHydrated: true });
        void applyLanguage(user.language);
        return;
      } catch {
        // Invalid JSON — fall through to the clear-and-mark-hydrated path.
      }
    }

    // No valid session (missing, expired, or unparseable). Clear any stale
    // values and mark hydration complete so guards stop showing the loading
    // state and can redirect unauthenticated users to /login.
    if (token || userJson) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
    set({ hasHydrated: true });
    void applyLanguage(preAccountLanguage());
  },

  updateUser(patch: Partial<User>) {
    set((state) => {
      if (!state.user) return state;
      const merged = { ...state.user, ...patch };
      try {
        localStorage.setItem('user', JSON.stringify(merged));
      } catch {
        // localStorage unavailable — best-effort
      }
      return { user: merged };
    });
  },

  async updateProfile(patch: {
    date_format?: User['date_format'];
    weight_unit?: User['weight_unit'];
    lab_unit?: User['lab_unit'];
    language?: User['language'];
    full_name?: string;
  }) {
    // Persist server-side, then trust the authoritative response for the
    // in-memory user so a single state update reflects the change app-wide.
    const updated = await api.updateProfile(patch);
    try {
      localStorage.setItem('user', JSON.stringify(updated));
    } catch {
      // localStorage unavailable — best-effort
    }
    set({ user: updated });
    await applyLanguage(updated.language);
  },

  clearError() {
    set({ error: null });
  },
}));
